/**
 * Billing API service — Plan & Coins tab client functions.
 *
 * Implements US-012 / T2: four typed async service functions covering the top-up
 * pack list endpoint and the three TS-011 Stripe action endpoints.
 *
 * Pattern: follows parent.ts convention exactly:
 *   - Import the shared axios instance from '@/lib/api'
 *   - No try/catch — errors propagate to React Query / the calling component
 *     so isError state surfaces correctly (KB TO-LL-036 / CCM-03 fail-safe).
 *   - Return only the relevant data field, not the raw AxiosResponse.
 *
 * TO-LL-044: all request/response field names are exact camelCase as per the
 * backend contracts (packId, successUrl, cancelUrl, returnUrl, checkoutUrl, portalUrl).
 *
 * CCM-03: these functions are called only from PlanAndCoinsTab, which mounts only
 * under ParentGuard — students never reach this surface.
 */

import { api } from '@/lib/api';
import type { TopupPack, SubscriptionPlansResponse } from '@/types/billing';

/**
 * Fetch the subscription plans (tiers) from GET /system-config/subscription-plans.
 *
 * Config-driven (CCM-04) — mirrors getTopupPacks. Returns the plans plus the shared
 * statementDescriptor. The response never carries Stripe price IDs (the client sends
 * a planKey to checkout; the backend maps it to the allowlisted price).
 * See Requirements/PRD-Subscription-Tiers.md §2/§4.
 * Errors propagate — never swallowed (KB TO-LL-036).
 */
export async function getSubscriptionPlans(): Promise<SubscriptionPlansResponse> {
  // BUG-20260702-205850: repointed from the non-existent '/system-config/subscription-plans'
  // to the real deployed route '/stripe/plans' (StripeCheckoutFunction) - 2026-07-02
  const response = await api.get<SubscriptionPlansResponse>('/stripe/plans');
  return response.data;
}

/**
 * Fetch the list of top-up pack definitions from GET /stripe/topup-packs
 * (BUG-20260703-012526: live-priced from Stripe, config fallback on failure).
 *
 * Implements US-012 / T2 / AC-5: packs are config-driven from the response;
 * no pack data is hardcoded in the frontend (CCM-04 label_site).
 *
 * The shared axios instance attaches Authorization: Bearer {idToken} automatically.
 * Errors propagate — never swallowed (KB TO-LL-036).
 *
 * @returns Promise resolving to an array of TopupPack objects
 */
export async function getTopupPacks(): Promise<TopupPack[]> {
  // BUG-20260703-012526: repointed from the config-only '/system-config/topup-packs'
  // to the live-priced '/stripe/topup-packs' (StripeCheckoutFunction) so the
  // displayed price is resolved from Stripe (config fallback), matching what
  // checkout actually charges - 2026-07-03
  const response = await api.get<{ packs: TopupPack[] }>('/stripe/topup-packs');
  return response.data.packs;
}

/**
 * Create a Stripe subscription checkout session.
 *
 * POST /stripe/checkout/subscription {plan, successUrl, cancelUrl}
 * Returns the checkoutUrl to redirect the parent to Stripe (full-page redirect).
 *
 * Implements US-012 / T2 / AC-2, extended for tiers (PRD-Subscription-Tiers.md §3.3).
 * The `plan` is a planKey ('starter' | 'practice' | 'family'); the backend maps it
 * to the allowlisted Stripe price. A client MUST NOT send a raw price id.
 * TO-LL-044: 'plan' / 'successUrl' / 'cancelUrl' / 'checkoutUrl' are exact camelCase keys.
 * Errors propagate (KB TO-LL-036 — never swallowed here).
 *
 * @param plan       - The tier planKey to subscribe to ('starter' | 'practice' | 'family')
 * @param successUrl - URL Stripe redirects back to on success (window.location.origin + ROUTES.PARENT_DASHBOARD)
 * @param cancelUrl  - URL Stripe redirects back to on cancel (same as successUrl for this app)
 * @returns Promise resolving to the Stripe-hosted checkout URL
 */
export async function createSubscriptionCheckout(
  plan: string,
  successUrl: string,
  cancelUrl: string
): Promise<string> {
  const response = await api.post<{ checkoutUrl: string }>('/stripe/checkout/subscription', {
    plan,
    successUrl,
    cancelUrl,
  });
  return response.data.checkoutUrl;
}

/**
 * Create a Stripe one-time top-up checkout session for a specific pack.
 *
 * POST /stripe/checkout/topup {packId, successUrl, cancelUrl}
 * Returns the checkoutUrl to redirect the parent to Stripe (full-page redirect).
 *
 * Implements US-012 / T2 / AC-2.
 * TO-LL-044: 'packId' / 'successUrl' / 'cancelUrl' / 'checkoutUrl' are exact camelCase keys.
 * Errors propagate (KB TO-LL-036 — never swallowed here).
 *
 * @param packId     - The pack identifier (e.g. 'pack_5') from the TopupPack config
 * @param successUrl - URL Stripe redirects back to on success
 * @param cancelUrl  - URL Stripe redirects back to on cancel
 * @returns Promise resolving to the Stripe-hosted checkout URL
 */
export async function createTopupCheckout(
  packId: string,
  successUrl: string,
  cancelUrl: string
): Promise<string> {
  const response = await api.post<{ checkoutUrl: string }>('/stripe/checkout/topup', {
    packId,
    successUrl,
    cancelUrl,
  });
  return response.data.checkoutUrl;
}

/**
 * Create a Stripe customer portal session for subscription management / cancellation.
 *
 * POST /stripe/portal {returnUrl} or {returnUrl, flow: 'change_plan'}
 * Returns the portalUrl to open in a new tab via window.open(_blank).
 *
 * Implements US-012 / T2 / AC-3, extended by TS-002 (change-plans) / T3 with an
 * optional `flow` deep-link hint. When flow === 'change_plan', the returned portal
 * session opens directly on the Stripe plan-switch screen instead of portal home.
 * The literal-union type ('change_plan' | undefined) means no arbitrary plan/price
 * string can ever be passed here (SEC-01 / AC-TS002.3) — a typo is a compile error.
 * flow is only included in the POST body when provided; when omitted, the request
 * body is byte-identical to today's { returnUrl } (AC-TS002.2 / backward-compatible
 * with the existing single-arg call site in PlanAndCoinsTab.tsx).
 *
 * NOTE: this story (TS-002) only makes the client function accept flow. Wiring an
 * actual 'Change plan' CTA that passes flow='change_plan' is US-001's job.
 *
 * TO-LL-044: 'returnUrl' / 'flow' / 'portalUrl' are exact camelCase contract keys.
 * TO-LL-007: keep reading response.data.portalUrl — do not flatten or rename.
 * Errors propagate (KB TO-LL-036). A 404 NOT_FOUND means no stripeCustomerId yet
 * (parent has not subscribed) or, for flow='change_plan', no active subscription;
 * the calling component decides how to surface it (US-001).
 *
 * @param returnUrl - URL the portal returns to when the parent is done (ROUTES.PARENT_DASHBOARD)
 * @param flow - Optional deep-link hint. 'change_plan' opens the portal directly on
 *   the plan-switch screen; omit for the existing bare portal-home behaviour.
 * @returns Promise resolving to the Stripe customer portal URL
 */
export async function createBillingPortal(
  returnUrl: string,
  flow?: 'change_plan'
): Promise<string> {
  const body = flow ? { returnUrl, flow } : { returnUrl };
  const response = await api.post<{ portalUrl: string }>('/stripe/portal', body);
  return response.data.portalUrl;
}
